import React, { useState, useEffect, useRef } from "react"
import { useNavigate } from "react-router-dom"
import {
  Row, Col, Card, CardBody, Button, Spinner, Alert,
  FormGroup, Label, Input,
} from "reactstrap"
import Switch from "react-switch"
import { connect } from "react-redux"
import { setBreadcrumbItems } from "../../store/actions"
import axiosClient from "../../helpers/axiosClient"
import { uploadToS3 } from "../../helpers/s3Upload"
import "./Empresas.scss"

const CrearEmpresa = ({ setBreadcrumbItems, user }) => {
  document.title = "Crear Empresa | LS-DA - Softlogy"

  const navigate = useNavigate()

  const [empresa, setEmpresa] = useState({
    nombre: "",
    nit: "",
    digVerificacion: "",
    email: "",
    telefono: "",
    direccion: "",
    logoUrl: "",
    logo2Url: "",
    logo3Url: "",
    logo4Url: "",
    logo5Url: "",
    activo: true,
    esAlianza: false,
    empresaPadreId: null,
    pais: "CO",
    departamento: "",
    ciudad: "",
    municipio: "",
    codDepartamento: "",
    codCiudad: "",
    regimen: "",
    obFiscales: "",
    obImpuestos: "",
    tipoPersona: "",
    tipoOperacion: "",
    leyenda: "",
    pinSoftware: "",
    idSoftware: "",
    certificadoRuta: "",
    certificadoClave: "",
    proveedorTecnologico: "",
    sqlServerId: null,
    emailContacto: "",
    tipoPdf: "",
    idSoftwareNomina: "",
    setIdNomina: "",
    setIdFactura: "",
    habilitado: false,
    habilitadonomina: false,
    notasPdf: "",
  })

  const [saving, setSaving] = useState(false)
  const [alert, setAlert] = useState({ show: false, message: "", variant: "" })
  const [showCertClave, setShowCertClave] = useState(false)
  const [uploading, setUploading] = useState({})
  const logoInputRefs = [useRef(null), useRef(null), useRef(null), useRef(null), useRef(null)]
  const logoFields = ["logoUrl", "logo2Url", "logo3Url", "logo4Url", "logo5Url"]
  const logoLabels = ["Logo Principal", "Logo 2", "Logo 3", "Logo 4", "Logo 5"]
  const [alianzas, setAlianzas] = useState([])
  const [userEmpresas, setUserEmpresas] = useState([])

  // Fetch user's empresas from API to determine alianza status
  useEffect(() => {
    const fetchUserEmpresas = async () => {
      if (user?.esSuperAdmin) return // SuperAdmin doesn't need this
      try {
        const res = await axiosClient.get("empresas/mis-empresas")
        const data = res.data?.data || res.data || []
        setUserEmpresas(Array.isArray(data) ? data : [])
      } catch { setUserEmpresas([]) }
    }
    fetchUserEmpresas()
  }, [user])

  // Determine alianza visibility rules
  const userAlianza = userEmpresas.find(e => e.esAlianza === true)
  const isAdminOfAlianza = !!userAlianza
  // Show alianza field: superAdmin always, admin of alianza (locked), others: never
  const showAlianzaField = user?.esSuperAdmin || isAdminOfAlianza

  // Pre-set empresaPadreId if user belongs to an alianza
  useEffect(() => {
    if (userAlianza && !empresa.empresaPadreId) {
      setField("empresaPadreId", userAlianza.id)
    }
  }, [userAlianza]) // eslint-disable-line react-hooks/exhaustive-deps

  // Dropdowns from XML
  const [paises, setPaises] = useState([])
  const [departamentos, setDepartamentos] = useState([])
  const [ciudades, setCiudades] = useState([])
  const [regimenes, setRegimenes] = useState([])
  const [obligacionesFiscales, setObligacionesFiscales] = useState([])
  const [obligacionesImpuestos, setObligacionesImpuestos] = useState([])
  const [tiposPersona, setTiposPersona] = useState([])

  // Breadcrumb
  useEffect(() => {
    const greeting = user
      ? `Bienvenido, ${user.nombre} ${user.apellido}`
      : "Dashboard"
    setBreadcrumbItems(greeting, [
      { title: "LS-DA", link: "#" },
      { title: "Empresas", link: "#" },
      { title: "Crear Empresa", link: "#" },
    ])
  }, [setBreadcrumbItems, user])

  // Generic XML list parser
  const fetchXmlList = async (url) => {
    const response = await fetch(url)
    if (!response.ok) {
      console.error(`[fetchXmlList] HTTP ${response.status} for ${url}`)
      return []
    }
    const contentType = response.headers.get("content-type") || ""
    const xmlText = await response.text()
    // Detect SPA redirect: Amplify returns index.html instead of XML
    if (contentType.includes("text/html") || xmlText.trimStart().startsWith("<!doctype") || xmlText.trimStart().startsWith("<!DOCTYPE")) {
      console.error(`[fetchXmlList] Got HTML instead of XML for ${url} — check Amplify rewrite rules`)
      return []
    }
    const parser = new DOMParser()
    const xmlDoc = parser.parseFromString(xmlText, "application/xml")
    const parseError = xmlDoc.querySelector("parsererror")
    if (parseError) {
      console.error(`[fetchXmlList] XML parse error for ${url}:`, parseError.textContent)
      return []
    }
    const rows = xmlDoc.getElementsByTagName("Row")
    const items = []
    for (let row of rows) {
      const code = row.querySelector('[ColumnRef="code"] SimpleValue')
      const name = row.querySelector('[ColumnRef="name"] SimpleValue')
      if (code && name) items.push({ code: code.textContent, name: name.textContent })
    }
    return items
  }

  const fetchDropdowns = async () => {
    try { setPaises(await fetchXmlList("/listas/Paises-2.1.xml")) } catch (e) { }
    try { setDepartamentos(await fetchXmlList("/listas/Departamentos-2.1.xml")) } catch (e) { }
    try { setRegimenes(await fetchXmlList("/listas/TipoRegimen-2.1.xml")) } catch (e) { }
    try { setObligacionesFiscales(await fetchXmlList("/listas/TipoResponsabilidad-2.1.xml")) } catch (e) { }
    try { setObligacionesImpuestos(await fetchXmlList("/listas/ObligacionesImpuestos-2.1.xml")) } catch (e) { }
    try { setTiposPersona(await fetchXmlList("/listas/TipoOrganizacion-2.1.xml")) } catch (e) { }
  }

  // Fetch ciudades filtered by departamento code
  const fetchCiudades = async (dep) => {
    if (!dep) return
    try {
      const all = await fetchXmlList("/listas/Municipio-2.1.xml")
      setCiudades(all.filter(c => c.code.startsWith(dep)))
    } catch (e) { /* ignore */ }
  }

  // Fetch alianzas
  const fetchAlianzas = async () => {
    try {
      const response = await axiosClient.get("empresas/alianzas")
      const data = response.data.data || response.data
      setAlianzas(Array.isArray(data) ? data : [])
    } catch (e) { /* ignore */ }
  }

  useEffect(() => { fetchDropdowns(); fetchAlianzas() }, [])
  useEffect(() => { if (empresa.codDepartamento) fetchCiudades(empresa.codDepartamento) }, [empresa.codDepartamento])

  // Update field
  const setField = (field, value) => {
    setEmpresa(prev => ({ ...prev, [field]: value }))
  }

  // Save
  const handleSubmit = async () => {
    if (!empresa.nombre || !empresa.nit) {
      setAlert({ show: true, message: "Nombre y NIT son obligatorios", variant: "danger" })
      return
    }
    if (!empresa.tipoPersona) {
      setAlert({ show: true, message: "Tipo de Persona es obligatorio", variant: "danger" })
      return
    }
    setSaving(true)
    try {
      const payload = { ...empresa }
      // Clean up null/empty fields
      if (!payload.sqlServerId) delete payload.sqlServerId
      if (!payload.empresaPadreId) delete payload.empresaPadreId
      // Fixed values
      payload.municipio = payload.ciudad || ""
      payload.tipoOperacion = "10"
      payload.pinSoftware = "70963"
      payload.proveedorTecnologico = "Supplier"
      payload.certificadoRuta = `/home/Colombia/Certificados/${payload.nit}/${payload.nit}.pfx`

      const response = await axiosClient.post("empresas", payload)
      const created = response.data.data || response.data
      setAlert({ show: true, message: `Empresa "${created.nombre}" creada exitosamente`, variant: "success" })
      setTimeout(() => {
        navigate(`/portal/empresa/${created.id}`)
      }, 2000)
    } catch (error) {
      const resData = error.response?.data
      let errorMsg = ""

      // Try to extract field-level validation errors
      if (resData?.errors && Array.isArray(resData.errors)) {
        // Format: [{ campo: "body.codigo", mensaje: "..." }, ...] or [{ field: "...", message: "..." }]
        errorMsg = resData.errors.map(e => {
          const fieldName = e.campo || e.field || ""
          const fieldMsg = e.mensaje || e.message || JSON.stringify(e)
          // Clean up field name (remove "body." prefix)
          const cleanField = fieldName.replace(/^body\./, "")
          return cleanField ? `• ${cleanField}: ${fieldMsg}` : `• ${fieldMsg}`
        }).join("\n")
      } else if (resData?.errors && typeof resData.errors === "object") {
        // Format: { email: "...", nit: "..." }
        errorMsg = Object.entries(resData.errors).map(([field, msg]) =>
          `• ${field}: ${Array.isArray(msg) ? msg.join(", ") : msg}`
        ).join("\n")
      } else if (resData?.details) {
        errorMsg = typeof resData.details === "string" ? resData.details : JSON.stringify(resData.details, null, 2)
      }

      const mainMsg = resData?.message || "Error al crear la empresa"
      const fullMsg = errorMsg ? `${mainMsg}\n${errorMsg}` : mainMsg

      setAlert({ show: true, message: typeof fullMsg === "string" ? fullMsg : JSON.stringify(fullMsg), variant: "danger" })
    } finally {
      setSaving(false)
    }
  }

  const inputStyle = { borderRadius: 8, border: "1px solid #e2e8f0", padding: "8px 12px", fontSize: "0.9rem" }
  const labelStyle = { fontWeight: 600, fontSize: "0.85rem", color: "#334155" }
  const sectionTitle = { fontWeight: 700, color: "#1e293b", marginBottom: 16, marginTop: 8, display: "flex", alignItems: "center", gap: 8 }

  return (
    <React.Fragment>
      <Row>
        <Col className="col-12">
          <Card style={{ border: "none", borderRadius: 16, boxShadow: "0 4px 24px rgba(0,0,0,0.06)" }}>
            <CardBody style={{ padding: "2rem" }}>
              {/* Header */}
              <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
                <div>
                  <h4 className="mb-1" style={{ fontWeight: 700 }}>
                    <i className="mdi mdi-plus-circle-outline me-2" style={{ color: "#556ee6" }} />
                    Crear Nueva Empresa
                  </h4>
                  <small className="text-muted">Complete los datos para crear una nueva empresa en el sistema</small>
                </div>
                <div className="d-flex gap-2">
                  <Button color="primary" onClick={handleSubmit} disabled={saving} style={{ borderRadius: 8, minWidth: 160 }}>
                    {saving ? <><Spinner size="sm" className="me-1" />Creando...</> : <><i className="mdi mdi-content-save me-1" />Crear Empresa</>}
                  </Button>
                  <Button color="outline-secondary" onClick={() => navigate("/portal/consultar-empresas")} style={{ borderRadius: 8 }}>
                    <i className="mdi mdi-arrow-left me-1" />Volver
                  </Button>
                </div>
              </div>

              {/* Alert */}
              {alert.show && (
                <Alert color={alert.variant} className="mb-3" toggle={() => setAlert({ show: false })}>
                  <i className={`mdi ${alert.variant === "success" ? "mdi-check-circle" : "mdi-alert-circle"} me-1`} />
                  <span style={{ whiteSpace: "pre-line" }}>{alert.message}</span>
                </Alert>
              )}

              {/* ── Información General ── */}
              <h5 style={sectionTitle}><i className="mdi mdi-information-outline" style={{ color: "#556ee6" }} />Información General</h5>
              <Row className="mb-3">
                <Col md={4}>
                  <FormGroup>
                    <Label style={labelStyle}>Nombre *</Label>
                    <Input type="text" style={inputStyle} value={empresa.nombre} onChange={e => setField("nombre", e.target.value)} placeholder="Nombre de la empresa" />
                  </FormGroup>
                </Col>
                <Col md={2}>
                  <FormGroup>
                    <Label style={labelStyle}>NIT *</Label>
                    <Input type="text" style={inputStyle} value={empresa.nit} onChange={e => setField("nit", e.target.value)} placeholder="Ej: 901921762" />
                  </FormGroup>
                </Col>
                <Col md={1}>
                  <FormGroup>
                    <Label style={labelStyle}>DV</Label>
                    <Input type="text" style={inputStyle} value={empresa.digVerificacion} onChange={e => setField("digVerificacion", e.target.value)} placeholder="4" />
                  </FormGroup>
                </Col>
                <Col md={2}>
                  <FormGroup>
                    <Label style={labelStyle}>Estado</Label>
                    <div className="d-flex align-items-center gap-2 mt-1">
                      <Switch onColor="#02a499" offColor="#dc3545" onChange={() => setField("activo", !empresa.activo)} checked={empresa.activo} height={22} width={44} />
                      <span style={{ fontWeight: 600, fontSize: "0.85rem", color: empresa.activo ? "#059669" : "#dc3545" }}>
                        {empresa.activo ? "Activo" : "Inactivo"}
                      </span>
                    </div>
                  </FormGroup>
                </Col>
              </Row>

              {/* ── Alianza (solo SuperAdmin) ── */}
              {user?.esSuperAdmin && (
                <Row className="mb-3">
                  <Col md={3}>
                    <FormGroup>
                      <Label style={labelStyle}>
                        <i className="mdi mdi-shield-star-outline me-1" style={{ color: "#556ee6" }} />
                        Es Alianza
                      </Label>
                      <div className="d-flex align-items-center gap-2 mt-1">
                        <Switch onColor="#556ee6" onChange={() => setField("esAlianza", !empresa.esAlianza)} checked={empresa.esAlianza} height={22} width={44} />
                        <span style={{ fontWeight: 600, fontSize: "0.85rem", color: empresa.esAlianza ? "#556ee6" : "#64748b" }}>
                          {empresa.esAlianza ? "Sí, esta empresa es una Alianza" : "No"}
                        </span>
                      </div>
                    </FormGroup>
                  </Col>
                  {!empresa.esAlianza && (
                    <Col md={4}>
                      <FormGroup>
                        <Label style={labelStyle}>
                          <i className="mdi mdi-link-variant me-1" />
                          Alianza (Empresa Padre)
                        </Label>
                        <Input type="select" style={inputStyle} value={empresa.empresaPadreId || ""}
                          onChange={e => setField("empresaPadreId", e.target.value || null)}>
                          <option value="">Sin alianza</option>
                          {alianzas.map(a => (
                            <option key={a.id} value={a.id}>{a.nombre} (NIT: {a.nit})</option>
                          ))}
                        </Input>
                      </FormGroup>
                    </Col>
                  )}
                </Row>
              )}

              {/* Alianza selector para admin de alianza (no superAdmin) */}
              {!user?.esSuperAdmin && showAlianzaField && !empresa.esAlianza && (
                <Row className="mb-3">
                  <Col md={4}>
                    <FormGroup>
                      <Label style={labelStyle}>
                        <i className="mdi mdi-link-variant me-1" />
                        Alianza (Empresa Padre)
                      </Label>
                      <Input type="text" style={{ ...inputStyle, backgroundColor: "#f1f5f9" }}
                        value={userAlianza?.nombre || "Sin alianza"}
                        disabled />
                    </FormGroup>
                  </Col>
                </Row>
              )}

              <hr />

              {/* ── Contacto ── */}
              <h5 style={sectionTitle}><i className="mdi mdi-email-outline" style={{ color: "#556ee6" }} />Contacto</h5>
              <Row className="mb-3">
                <Col md={4}>
                  <FormGroup>
                    <Label style={labelStyle}>Email</Label>
                    <Input type="email" style={inputStyle} value={empresa.email} onChange={e => setField("email", e.target.value)} placeholder="correo@empresa.com" />
                  </FormGroup>
                </Col>
                <Col md={4}>
                  <FormGroup>
                    <Label style={labelStyle}>Email Contacto</Label>
                    <Input type="email" style={inputStyle} value={empresa.emailContacto} onChange={e => setField("emailContacto", e.target.value)} placeholder="contacto@empresa.com" />
                  </FormGroup>
                </Col>
                <Col md={2}>
                  <FormGroup>
                    <Label style={labelStyle}>Teléfono</Label>
                    <Input type="text" style={inputStyle} value={empresa.telefono} onChange={e => setField("telefono", e.target.value)} placeholder="3001234567" />
                  </FormGroup>
                </Col>
                <Col md={2}>
                  <FormGroup>
                    <Label style={labelStyle}>Tipo PDF</Label>
                    <Input type="select" style={inputStyle} value={empresa.tipoPdf} onChange={e => setField("tipoPdf", e.target.value)}>
                      <option value="">Seleccione</option>
                      <option value="V1">V1</option>
                      <option value="V2">V2</option>
                      <option value="V3">V3</option>
                      <option value="V4">V4</option>
                      <option value="V5">V5</option>
                      <option value="V6">V6</option>
                      <option value="V7">V7</option>
                    </Input>
                  </FormGroup>
                </Col>
              </Row>
              <Row className="mb-3">
                <Col md={5}>
                  <FormGroup>
                    <Label style={labelStyle}>Dirección</Label>
                    <Input type="text" style={inputStyle} value={empresa.direccion} onChange={e => setField("direccion", e.target.value)} placeholder="CL 81 NO. 11 94" />
                  </FormGroup>
                </Col>
              </Row>

              <hr />

              {/* ── Ubicación ── */}
              <h5 style={sectionTitle}><i className="mdi mdi-map-marker-outline" style={{ color: "#556ee6" }} />Ubicación</h5>
              <Row className="mb-3">
                <Col md={2}>
                  <FormGroup>
                    <Label style={labelStyle}>País</Label>
                    <Input type="select" style={inputStyle} value={empresa.pais}
                      onChange={e => setField("pais", e.target.value)}>
                      <option value="">Seleccione</option>
                      {paises.map(p => <option key={p.code} value={p.code}>{p.name}</option>)}
                    </Input>
                  </FormGroup>
                </Col>
                <Col md={3}>
                  <FormGroup>
                    <Label style={labelStyle}>Departamento</Label>
                    <Input type="select" style={inputStyle} value={empresa.codDepartamento}
                      onChange={e => {
                        const dep = departamentos.find(d => d.code === e.target.value)
                        setEmpresa(prev => ({ ...prev, codDepartamento: e.target.value, departamento: dep?.name || e.target.value, codCiudad: "", ciudad: "", municipio: "" }))
                      }}>
                      <option value="">Seleccione</option>
                      {departamentos.map(d => <option key={d.code} value={d.code}>{d.name}</option>)}
                    </Input>
                  </FormGroup>
                </Col>
                <Col md={3}>
                  <FormGroup>
                    <Label style={labelStyle}>Ciudad</Label>
                    <Input type="select" style={inputStyle} value={empresa.codCiudad}
                      onChange={e => {
                        const city = ciudades.find(c => c.code === e.target.value)
                        setEmpresa(prev => ({ ...prev, codCiudad: e.target.value, ciudad: city?.name || e.target.value }))
                      }}>
                      <option value="">Seleccione</option>
                      {ciudades.map(c => <option key={c.code} value={c.code}>{c.name}</option>)}
                    </Input>
                  </FormGroup>
                </Col>

              </Row>

              <hr />

              {/* ── Datos Fiscales / DIAN ── */}
              <h5 style={sectionTitle}><i className="mdi mdi-file-document-outline" style={{ color: "#556ee6" }} />Datos Fiscales / DIAN</h5>
              <Row className="mb-3">
                <Col md={3}>
                  <FormGroup>
                    <Label style={labelStyle}>Régimen</Label>
                    <Input type="select" style={inputStyle} value={empresa.regimen}
                      onChange={e => setField("regimen", e.target.value)}>
                      <option value="">Seleccione</option>
                      {regimenes.map(r => <option key={r.code} value={r.code}>{r.name}</option>)}
                    </Input>
                  </FormGroup>
                </Col>
                <Col md={3}>
                  <FormGroup>
                    <Label style={labelStyle}>Obligaciones Fiscales</Label>
                    <Input type="select" style={inputStyle} value={empresa.obFiscales}
                      onChange={e => setField("obFiscales", e.target.value)}>
                      <option value="">Seleccione</option>
                      {obligacionesFiscales.map(o => <option key={o.code} value={o.code}>{o.name}</option>)}
                    </Input>
                  </FormGroup>
                </Col>
                <Col md={3}>
                  <FormGroup>
                    <Label style={labelStyle}>Obligaciones Impuestos</Label>
                    <Input type="select" style={inputStyle} value={empresa.obImpuestos}
                      onChange={e => setField("obImpuestos", e.target.value)}>
                      <option value="">Seleccione</option>
                      {obligacionesImpuestos.map(o => <option key={o.code} value={o.code}>{o.name}</option>)}
                    </Input>
                  </FormGroup>
                </Col>

                <Col md={3}>
                  <FormGroup>
                    <Label style={labelStyle}>Tipo Persona</Label>
                    <Input type="select" style={inputStyle} value={empresa.tipoPersona || ""}
                      onChange={e => setField("tipoPersona", e.target.value)}>
                      <option value="">Seleccione</option>
                      {tiposPersona.map(t => <option key={t.code} value={t.code}>{t.name}</option>)}
                    </Input>
                  </FormGroup>
                </Col>
              </Row>

              <hr />

              {/* ── Parámetros de Empresa ── */}
              <h5 style={sectionTitle}><i className="mdi mdi-tune-vertical" style={{ color: "#556ee6" }} />Parámetros de Empresa</h5>
              <Row className="mb-3">
                <Col md={4}>
                  <FormGroup>
                    <Label style={labelStyle}>Leyenda</Label>
                    <Input type="text" style={inputStyle} value={empresa.leyenda} onChange={e => setField("leyenda", e.target.value)} placeholder="Nombre corto" />
                  </FormGroup>
                </Col>
                <Col md={8}>
                  <FormGroup>
                    <Label style={labelStyle}>Notas PDF</Label>
                    <Input type="textarea" style={{ ...inputStyle, minHeight: 80, resize: "vertical" }}
                      value={empresa.notasPdf} maxLength={250}
                      onChange={e => setField("notasPdf", e.target.value)}
                      placeholder="Notas que aparecerán en el PDF (máx. 250 caracteres)" />
                    <small style={{ color: "#94a3b8", fontSize: 11 }}>{(empresa.notasPdf || "").length}/250 caracteres</small>
                  </FormGroup>
                </Col>
              </Row>

              <hr />

              {/* ── Software & Certificado ── */}
              <h5 style={sectionTitle}><i className="mdi mdi-cog-outline" style={{ color: "#556ee6" }} />Software & Certificado</h5>
              <Row className="mb-3">
                <Col md={12}>
                  <Row className="mb-3">
                    <Col md={4}>
                      <FormGroup>
                        <Label style={labelStyle}>ID Software Facturación</Label>
                        <Input type="text" style={inputStyle} value={empresa.idSoftware} onChange={e => setField("idSoftware", e.target.value)} placeholder="UUID del software" />
                      </FormGroup>
                    </Col>
                    <Col md={4}>
                      <FormGroup>
                        <Label style={labelStyle}>Set ID Factura</Label>
                        <Input type="text" style={inputStyle} value={empresa.setIdFactura} onChange={e => setField("setIdFactura", e.target.value)} placeholder="Set ID Factura" />
                      </FormGroup>
                    </Col>
                  </Row>
                  <Row className="mb-3">
                    <Col md={4}>
                      <FormGroup>
                        <Label style={labelStyle}>ID Software Nómina</Label>
                        <Input type="text" style={inputStyle} value={empresa.idSoftwareNomina} onChange={e => setField("idSoftwareNomina", e.target.value)} placeholder="UUID software nómina" />
                      </FormGroup>
                    </Col>
                    <Col md={4}>
                      <FormGroup>
                        <Label style={labelStyle}>Set ID Nómina</Label>
                        <Input type="text" style={inputStyle} value={empresa.setIdNomina} onChange={e => setField("setIdNomina", e.target.value)} placeholder="Set ID Nómina" />
                      </FormGroup>
                    </Col>
                  </Row>
                  <Row className="mb-3">
                    <Col md={3}>
                      <FormGroup>
                        <Label style={labelStyle}>Certificado Clave</Label>
                        <div className="d-flex align-items-center gap-2">
                          <Input type={showCertClave ? "text" : "password"} style={inputStyle} value={empresa.certificadoClave}
                            onChange={e => setField("certificadoClave", e.target.value)} placeholder="Clave del certificado" />
                          <Button outline color="secondary" size="sm" onClick={() => setShowCertClave(!showCertClave)} style={{ borderRadius: 6, whiteSpace: "nowrap" }}>
                            {showCertClave ? "Ocultar" : "Mostrar"}
                          </Button>
                        </div>
                      </FormGroup>
                    </Col>
                    <Col md={12}>
                      <FormGroup>
                        <Label style={labelStyle}><i className="mdi mdi-image-multiple" style={{ color: "#556ee6" }} /> Logos de la Empresa (hasta 5)</Label>
                        <div style={{ fontSize: 11, color: "#6c757d", marginTop: 2, marginBottom: 10, background: "#f8f9fa", borderRadius: 6, padding: "6px 12px", border: "1px solid #e9ecef" }}>
                          <i className="mdi mdi-information-outline me-1" style={{ color: "#556ee6" }} />
                          <strong>Requisitos:</strong> Formato PNG, JPG o WEBP · Peso máximo 1 MB · Tamaño máximo 500 × 500 píxeles
                        </div>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
                          {logoFields.map((field, idx) => (
                            <div key={field} style={{
                              width: 140, minHeight: 140, border: empresa[field] ? "2px solid #34c38f" : "2px dashed #dee2e6",
                              borderRadius: 10, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
                              background: empresa[field] ? "#f0fdf4" : "#f8fafc", padding: 8, position: "relative", transition: "all 0.2s",
                            }}>
                              <div style={{ fontSize: 10, fontWeight: 600, color: "#6c757d", marginBottom: 4 }}>{logoLabels[idx]}</div>
                              {empresa[field] ? (
                                <>
                                  <img src={empresa[field]} alt={logoLabels[idx]} style={{ maxHeight: 60, maxWidth: "100%", objectFit: "contain", borderRadius: 4 }} />
                                  <button type="button" onClick={() => setField(field, "")}
                                    style={{ position: "absolute", top: 4, right: 4, background: "#dc3545", color: "#fff", border: "none", borderRadius: "50%", width: 20, height: 20, fontSize: 11, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", lineHeight: 1 }}
                                    title="Eliminar logo">×</button>
                                </>
                              ) : (
                                <>
                                  <i className="mdi mdi-image-plus" style={{ fontSize: 28, color: "#adb5bd" }} />
                                  <input type="file" accept=".png,.jpg,.jpeg,.webp,.svg" ref={logoInputRefs[idx]} style={{ display: "none" }}
                                    onChange={async e => {
                                      const file = e.target.files?.[0]
                                      if (!file) return
                                      // Validar formato
                                      const allowedTypes = ["image/png", "image/jpeg", "image/jpg", "image/webp", "image/svg+xml"]
                                      if (!allowedTypes.includes(file.type)) {
                                        setAlert({ show: true, message: `Formato no permitido. Use PNG, JPG o WEBP.`, variant: "danger" })
                                        e.target.value = null; return
                                      }
                                      // Validar peso (1 MB)
                                      if (file.size > 1 * 1024 * 1024) {
                                        setAlert({ show: true, message: `El archivo pesa ${(file.size / 1024 / 1024).toFixed(1)} MB. El máximo es 1 MB.`, variant: "danger" })
                                        e.target.value = null; return
                                      }
                                      // Validar dimensiones (500x500)
                                      if (file.type !== "image/svg+xml") {
                                        const valid = await new Promise(resolve => {
                                          const img = new Image()
                                          img.onload = () => resolve(img.width <= 500 && img.height <= 500)
                                          img.onerror = () => resolve(false)
                                          img.src = URL.createObjectURL(file)
                                        })
                                        if (!valid) {
                                          setAlert({ show: true, message: `La imagen excede 500×500 píxeles. Reduzca el tamaño antes de subirla.`, variant: "danger" })
                                          e.target.value = null; return
                                        }
                                      }
                                      setUploading(prev => ({ ...prev, [field]: true }))
                                      try {
                                        const suffix = idx > 0 ? `_logo${idx + 1}` : ""
                                        const url = await uploadToS3(file, "logos", `${empresa.nit || empresa.codigo || Date.now()}${suffix}`)
                                        setField(field, url)
                                      } catch (err) {
                                        setAlert({ show: true, message: `Error al subir ${logoLabels[idx]}: ` + err.message, variant: "danger" })
                                      } finally {
                                        setUploading(prev => ({ ...prev, [field]: false }))
                                        e.target.value = null
                                      }
                                    }} />
                                  <Button color="primary" size="sm" disabled={uploading[field]}
                                    onClick={() => logoInputRefs[idx].current?.click()}
                                    style={{ borderRadius: 6, fontSize: 10, padding: "2px 8px", marginTop: 4 }}>
                                    {uploading[field] ? <><Spinner size="sm" style={{ width: 12, height: 12 }} /> Subiendo...</> : "Subir"}
                                  </Button>
                                </>
                              )}
                            </div>
                          ))}
                        </div>
                      </FormGroup>
                    </Col>
                  </Row>
                </Col>
              </Row>


            </CardBody>
          </Card>
        </Col>
      </Row >
    </React.Fragment >
  )
}

const mapStateToProps = state => ({
  user: state.Login.user,
})

export default connect(mapStateToProps, { setBreadcrumbItems })(CrearEmpresa)